import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Building2, Mail, Save, UserRound, Users } from 'lucide-react'
import { ApplicantTabNavigation } from '@components/layout/ApplicantTabNavigation'
import { ApplicationHeader } from '@components/layout/ApplicationHeader'
import { useAppSelector } from '@/app/hooks'
import { selectApplicationData } from '@/features/applicant/applicationSlice'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import { inviteTabSnapshot } from '@/features/applicant/utils/inviteTabSnapshot'
import { inviteCheckboxClass } from '@/features/auth/components/inviteOnboardingUi'
import { getApplicationHeaderProps } from '@lib/utils/applicationHeaderHelpers'
import {
  getTabNavigationOptions,
  getTabPathWithApplicantId,
} from '@lib/utils/applicationNavigation'
import { cn } from '@lib/utils/helpers'
import { formatPhoneNumber, maskSSN } from '@/types/application'
import type { CoResident } from '@/types/application'

const lockedInputClass =
  'w-full cursor-default rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800'

function formatDob(value?: string) {
  if (!value) return ''
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) return `${match[2]}/${match[3]}/${match[1]}`
  return value
}

function formatRent(value: unknown) {
  if (value == null || value === '') return ''
  const amount = Number(value)
  if (Number.isNaN(amount)) return String(value)
  return `$${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}/mo`
}

function formatMoveIn(value?: string) {
  if (!value) return ''
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return value
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function coApplicantRows(rows: CoResident[] | undefined) {
  return (rows ?? []).filter((person) => person.first_name || person.last_name || person.email)
}

export function Invite() {
  const navigate = useNavigate()
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>()
  const applicationData = useAppSelector(selectApplicationData)
  const snapshot = useMemo(() => inviteTabSnapshot(applicationData), [applicationData])
  const personal = snapshot.personal ?? {}
  const property = snapshot.property ?? {}
  const coApplicants = coApplicantRows(applicationData?.co_resident)
  const sharedPhone = personal.shared_phone_no === '1' || personal.shared_phone_no === 'true'
  const preference = personal.communication_preference || ''
  const expected = property.expected_residents != null && String(property.expected_residents) !== ''
    ? String(property.expected_residents)
    : ''
  const phone = formatPhoneNumber(personal.mobile_no) || personal.mobile_no || ''
  const address = property.property_address || ''
  const applicantId = urlApplicantId || applicationData?.applicant_id

  const continueToAbout = () => {
    if (applicantId) {
      navigate(getTabPathWithApplicantId('about', applicantId), getTabNavigationOptions('about'))
      return
    }
    navigate('/applicant/application/about')
  }

  return (
    <div>
      <ApplicationHeader
        {...getApplicationHeaderProps(
          applicationData,
          APPLICANT_CONTENT.application.status.incomplete,
        )}
      />
      <ApplicantTabNavigation />
      <div className={applicationData?.applicant_lock === '1' ? 'form-readonly' : undefined}>
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[1.45fr_1fr]">
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6 md:p-8">
          <div className="mb-6 flex items-center gap-3 border-b border-gray-200 pb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <UserRound className="h-5 w-5 text-blue-600" aria-hidden />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Personal &amp; Contact Information</h2>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                First Name<span className="text-red-500">*</span>
              </label>
              <input readOnly tabIndex={-1} value={personal.first_name || ''} className={lockedInputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Middle Name</label>
              <input
                readOnly
                tabIndex={-1}
                value={personal.middle_name || ''}
                placeholder="Optional"
                className={lockedInputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Last Name<span className="text-red-500">*</span>
              </label>
              <input readOnly tabIndex={-1} value={personal.last_name || ''} className={lockedInputClass} />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Date of Birth<span className="text-red-500">*</span>
              </label>
              <input readOnly tabIndex={-1} value={formatDob(personal.birthdate)} className={lockedInputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                SSN / ITIN<span className="text-red-500">*</span>
              </label>
              <input readOnly tabIndex={-1} value={maskSSN(personal.security_number)} className={lockedInputClass} />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Email<span className="text-red-500">*</span>
              </label>
              <input readOnly tabIndex={-1} value={personal.email || ''} className={lockedInputClass} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Phone Number<span className="text-red-500">*</span>
              </label>
              <input readOnly tabIndex={-1} value={phone} className={lockedInputClass} />
            </div>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={sharedPhone} readOnly disabled className={inviteCheckboxClass} />
            This is a shared phone number.
          </label>

          <div className="mt-5">
            <p className="mb-2 text-sm font-medium text-slate-700">
              Communication Preference<span className="text-red-500">*</span>
            </p>
            <div className="inline-flex w-fit flex-wrap gap-2">
              {([
                ['Email', 'Email'],
                ['Text Msg', 'Text Message'],
              ] as const).map(([value, label]) => (
                <span
                  key={value}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium',
                    preference === value
                      ? 'border-button bg-button text-white'
                      : 'border-slate-300 bg-white text-slate-700',
                  )}
                >
                  {value === 'Email' ? <Mail className="h-4 w-4" aria-hidden /> : null}
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-md sm:p-6 md:p-8">
          <div className="mb-6 flex items-center justify-between gap-3 border-b border-gray-200 pb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <Building2 className="h-5 w-5 text-blue-600" aria-hidden />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Your Property</h2>
            </div>
            <span className="text-xl font-bold text-gray-900">Invitation details</span>
          </div>

          <div className="flex items-start justify-between gap-3 pb-4">
            <div>
              <p className="text-lg font-semibold text-slate-900">{property.property_name || 'Your Property'}</p>
              {address ? <p className="mt-1 text-sm text-slate-500">{address}</p> : null}
              {property.unit_number ? (
                <p className="text-sm text-slate-500">Unit {property.unit_number}</p>
              ) : null}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-slate-700">Monthly Rent</p>
              <p className="text-lg font-semibold text-emerald-600">{formatRent(property.rent_amount) || '—'}</p>
              {property.lease_start_date ? (
                <p className="text-xs text-slate-500">Move-in: {formatMoveIn(property.lease_start_date)}</p>
              ) : null}
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-4 border-t border-slate-200 py-4 text-sm">
            <div>
              <dt className="text-sm font-medium text-slate-700">Invite Code</dt>
              <dd className="mt-1 font-medium text-slate-800">{property.invite_code || '—'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-slate-700">Leasing Agent</dt>
              <dd className="mt-1 font-medium text-slate-800">{property.agent_name || '—'}</dd>
              {property.agent_email ? (
                <dd className="text-xs text-slate-500">{property.agent_email}</dd>
              ) : null}
            </div>
          </dl>

          {coApplicants.length > 0 ? (
            <div className="border-t border-slate-200 py-4">
              <p className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                <Users className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                Co-Applicants / Roommates
              </p>
              <div className="grid grid-cols-4 gap-3 border-b border-slate-200 pb-2 text-sm font-medium text-slate-700">
                <span>Name</span>
                <span>Relationship</span>
                <span>Email</span>
                <span>Phone</span>
              </div>
              {coApplicants.map((person, index) => (
                <div
                  key={`${person.email || person.mobile_no || index}`}
                  className="grid grid-cols-1 gap-2 border-b border-slate-200 py-3 text-sm last:border-b-0 sm:grid-cols-4 sm:gap-3"
                >
                  <p className="text-slate-800">
                    {[person.first_name, person.last_name].filter(Boolean).join(' ') || '—'}
                  </p>
                  <p className="text-slate-700">{person.relationship_type || '—'}</p>
                  <p className="break-all text-slate-700">{person.email || '—'}</p>
                  <p className="text-slate-700">
                    {formatPhoneNumber(person.mobile_no) || person.mobile_no || '—'}
                  </p>
                </div>
              ))}
            </div>
          ) : null}

          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-slate-700">
                Expected residents (including children and other roommates)
                <span className="text-red-500">*</span>
              </p>
              <select
                disabled
                value={expected}
                className="w-28 cursor-default rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
              >
                <option value={expected}>{expected || '—'}</option>
              </select>
            </div>
            <p className="mt-2 text-xs text-slate-500">This field is required to proceed.</p>
          </div>
        </section>
      </div>
      <div className="flex flex-wrap gap-3 pb-6 pt-4">
        <button
          type="button"
          onClick={continueToAbout}
          className="inline-flex items-center gap-2 rounded-lg bg-[#1597ff] px-8 py-3 font-medium text-white shadow-md transition-all hover:bg-[#0d7acc] hover:shadow-lg"
        >
          <Save className="h-4 w-4" aria-hidden />
          {APPLICANT_CONTENT.application.buttons.saveContinue}
        </button>
        <button
          type="button"
          onClick={() => navigate('/applicant/dashboard', { replace: true })}
          className="inline-flex items-center gap-2 rounded-lg bg-[#1597ff] px-8 py-3 font-medium text-white shadow-sm transition-all hover:bg-[#0d7acc] hover:shadow-md"
        >
          {APPLICANT_CONTENT.application.buttons.saveExit}
        </button>
      </div>
      </div>
      </div>
    </div>
  )
}
